import type { InnogyComponentRendering } from '@core/jss-models';
import type { ProgressiveFormStateImplementor } from '@innogy/progressive-ngrx-forms';
import {
  activateProgressiveFormStepOnSubmissionOf,
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
} from '@innogy/progressive-ngrx-forms';
import { on, type Action } from '@ngrx/store';
import { updateGroup, validate } from 'ngrx-forms';
import { requiredTrue } from 'ngrx-forms/validation';
import { validateSequentialIf } from '@innogy/utils-deprecated';

import {
  addOrderRenderingAction,
  setEnableAtoCheckAction,
} from '../order.actions';
import type { OrderState } from '../order.state';
import { ORDER_PAYMENT_FORM_ID } from '../payment';

export const ORDER_CONFIRMATION_FORM_ID: keyof OrderState =
  'orderConfirmationForm';
export const conditionsAcceptedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.conditionsAccepted`;
export const atoConsentedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.atoConsented`;
export const energyAdviceEmailRejectedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.energyAdviceEmailRejected`;
export const sustainabilityAdviceEmailRejectedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.sustainabilityAdviceEmailRejected`;
export const energyAdvicePhoneAcceptedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.energyAdvicePhoneAccepted`;
export const sustainabilityAdvicePhoneAcceptedFormControlID = `${ORDER_CONFIRMATION_FORM_ID}.sustainabilityAdvicePhoneAccepted`;

export interface OrderConfirmationFormValues {
  conditionsAccepted: boolean;
  atoConsented: boolean;
  energyAdviceEmailRejected: boolean;
  sustainabilityAdviceEmailRejected: boolean;
  energyAdvicePhoneAccepted: boolean;
  sustainabilityAdvicePhoneAccepted: boolean;
}

export interface OrderConfirmationState
  extends ProgressiveFormStateImplementor<OrderConfirmationFormValues> {
  rendering: InnogyComponentRendering | undefined;
  enableAtoCheck: boolean;
}

export const initialOrderConfirmationProgressiveFormState =
  createProgressiveFormGroupState<OrderConfirmationFormValues>(
    ORDER_CONFIRMATION_FORM_ID,
    {
      conditionsAccepted: false,
      atoConsented: false,
      energyAdviceEmailRejected: false,
      sustainabilityAdviceEmailRejected: false,
      energyAdvicePhoneAccepted: false,
      sustainabilityAdvicePhoneAccepted: false,
    }
  );

export const initialOrderConfirmationState: OrderConfirmationState = {
  progressiveForm: initialOrderConfirmationProgressiveFormState,
  rendering: undefined,
  enableAtoCheck: false,
};

const validateForm = (state: OrderConfirmationState) => {
  return updateGroup<OrderConfirmationFormValues>({
    conditionsAccepted: validate(requiredTrue),
    atoConsented: validateSequentialIf(state.enableAtoCheck)(requiredTrue),
  })(state.progressiveForm.formState);
};

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialOrderConfirmationState,
  validateForm,
  activateProgressiveFormStepOnSubmissionOf(ORDER_PAYMENT_FORM_ID),
  on(addOrderRenderingAction, (state, action) => ({
    ...state,
    rendering: action.rendering,
  })),
  on(setEnableAtoCheckAction, (state, action) => ({
    ...state,
    enableAtoCheck: action.enableAtoCheck,
  }))
);

export function orderConfirmationReducer(
  state: OrderConfirmationState = initialOrderConfirmationState,
  action: Action
): OrderConfirmationState {
  return _reducer(state, action);
}
